import { supabase } from './client';
import { createNotification } from './notifications';
import { getStoreById } from './stores';
import { formatCurrency } from '@/lib/utils';

export interface OrderItem {
  id: string;
  order_id: string;
  product_id: string;
  quantity: number;
  unit_price: number;
  details?: Record<string, any> | null;
}

export interface Order {
  id: string;
  store_id: string | null;
  user_id: string | null;
  total: number | null;
  status: string;
  notes?: string | null;
  created_at: string;
  updated_at: string;
  items?: OrderItem[];
  // Payment data from buyer
  buyer_cbu?: string | null;
  buyer_bank_name?: string | null;
  buyer_account_holder?: string | null;
  payment_method?: string | null;
}

export const getOrdersByStoreId = async (storeId: string): Promise<Order[]> => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('*, order_items(*)')
      .eq('store_id', storeId)
      .order('created_at', { ascending: false });
    if (error) {
      // If the `orders` table is missing in the connected Supabase schema
      // (PGRST205), surface a friendly log and return an empty list so the
      // UI can continue working. This avoids uncaught exceptions when the
      // DB migrations haven't been applied yet.
      console.error('getOrdersByStoreId error:', error);
      if ((error as any)?.code === 'PGRST205') {
        console.warn('getOrdersByStoreId: orders table not found; returning empty list');
        return [];
      }
      throw error;
    }
    return (data as any) || [];
  } catch (err) {
    console.error('getOrdersByStoreId error:', err);
    throw err;
  }
};

export const getOrdersByUserId = async (userId: string): Promise<Order[]> => {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('*, order_items(*), stores!fk_orders_store_id(name, slug, avatar_url)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (error) {
      console.error('getOrdersByUserId error:', error);

      // Fallback: some environments have a different FK name or no embedded relation metadata.
      // In that case, return orders + items without stores join instead of breaking checkout history.
      const fallback = await supabase
        .from('orders')
        .select('*, order_items(*)')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (!fallback.error) {
        return (fallback.data as any) || [];
      }

      if ((error as any)?.code === 'PGRST205') {
        console.warn('getOrdersByUserId: orders table not found; returning empty list');
        return [];
      }
      throw error;
    }
    return (data as any) || [];
  } catch (err) {
    console.error('getOrdersByUserId error:', err);
    throw err;
  }
};

export const getPendingOrdersCount = async (userId: string): Promise<number> => {
  try {
    const { count, error } = await supabase
      .from('orders')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .not('status', 'in', '(fulfilled,cancelled)');
    if (error) {
      console.error('getPendingOrdersCount error:', error);
      return 0;
    }
    return count || 0;
  } catch (err) {
    console.error('getPendingOrdersCount error:', err);
    return 0;
  }
};

// NOTE: `store_take_order` RPC was previously used to set taken_by/taken_at.
// We moved to a status-based approach ('assigned'). If you still have the
// RPC in the DB you can drop it; see repo SQL files for instructions.

export const updateOrderStatus = async (orderId: string, status: string) => {
  try {
    const resolveActorName = async (): Promise<string> => {
      try {
        const { data: authData } = await supabase.auth.getUser();
        const actorId = authData?.user?.id;
        if (!actorId) return 'La tienda';

        const { data: profile } = await supabase
          .from('profiles')
          .select('display_name, username')
          .eq('id', actorId)
          .maybeSingle();

        return profile?.display_name || profile?.username || 'La tienda';
      } catch {
        return 'La tienda';
      }
    };

    const actorName = await resolveActorName();

    // Prefer calling the secure RPC which enforces store membership on the server
    const { data: rpcData, error: rpcErr } = await supabase.rpc('store_update_order_status', { p_order_id: orderId, p_status: status });
    if (rpcErr) {
      // If RPC fails (missing on older DBs or other error), fall back to direct update.
      // We avoid throwing here to keep client functionality working in environments
      // where the RPC has not been deployed yet.
      // Fallback: try direct update (for older DBs without the RPC)
      const { data, error } = await supabase
        .from('orders')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', orderId)
        .select()
        .single();
      if (error) throw error;
      const dataToUse = data as any;
      // send notification to order owner if present
      try{
        const orderUserId = dataToUse?.user_id;
        if(orderUserId){
          const storeId = dataToUse?.store_id;
          let storeName = 'La tienda';
          if(storeId){
            try{
              const s = await getStoreById(storeId);
              if(s && s.name) storeName = s.name;
            }catch(e){ /* ignore */ }
          }

          const total = typeof dataToUse?.total === 'number' ? dataToUse.total : (dataToUse?.total ? Number(dataToUse.total) : null);
          const totalText = total != null ? formatCurrency(total) : 'un monto';

          // Obtener order_number si está disponible, si no, usar orderId como fallback
          const orderNumber = dataToUse?.order_number || orderId;
          const templates: Record<string,string> = {
            pending: `${actorName} marcó tu pedido #${orderNumber} como pendiente (${storeName}).`,
            assigned: `${actorName} está preparando tu pedido #${orderNumber} (${storeName}).`,
            assignment_requested: `${actorName} solicitó que te encargues un pedido (${storeName}).`,
            paid: `${actorName} confirmó que recibió una transferencia bancaria de ${totalText} para la orden #${orderNumber} (${storeName}).`,
            fulfilled: `${actorName} completó tu pedido #${orderNumber} (${storeName}).`,
            cancelled: `${actorName} canceló tu pedido (${storeName}).`,
            ready_for_pickup: `${actorName} indica que tu pedido #${orderNumber} está listo para ser retirado (${storeName}).`,
            shipping_in_progress: `${actorName} indica que tu pedido #${orderNumber} está en proceso de envío (${storeName}).`,
          };

          const message = templates[status] || `${actorName} actualizó el estado de tu pedido a: ${status} (${storeName}).`;
          await createNotification(orderUserId, 'order_update', message, `/orden/${orderId}`);
        }
      }catch(e){ console.warn('Failed to create order notification', e); }

      // Rental: solo actuar si rental_metadata es null (columna ausente/save falló).
      // Si rental_metadata está presente, el trigger DB on_store_rental_order_paid lo maneja.
      if (status === 'paid' && !dataToUse?.rental_metadata && dataToUse?.user_id) {
        try {
          const { data: itemRows } = await supabase
            .from('order_items')
            .select('quantity, products(id, is_store_rental, details)')
            .eq('order_id', orderId);
          const rentalItems = ((itemRows ?? []) as any[]).filter((oi: any) => {
            if (oi?.products?.is_store_rental === true) return true;
            try {
              const d = typeof oi?.products?.details === 'string'
                ? JSON.parse(oi.products.details)
                : oi?.products?.details;
              return d?.is_store_rental === true;
            } catch { return false; }
          });
          if (rentalItems.length > 0) {
            const paidDays = Math.max(
              rentalItems.reduce((s: number, oi: any) => s + (Number(oi.quantity) || 1), 0), 1
            );
            console.log('[StoreRental] direct-update order_items fallback: paidDays=', paidDays);
            const { error: rentalRpcErr } = await supabase.rpc('register_or_renew_store_rental', {
              p_user_id: dataToUse.user_id,
              p_order_id: orderId,
              p_paid_days: paidDays,
              p_store_id: null,
            });
            if (rentalRpcErr) {
              console.warn('[StoreRental] register_or_renew_store_rental (direct fallback) error:', rentalRpcErr);
            }
          }
        } catch (rentalErr) {
          console.warn('[StoreRental] direct-update rental fallback failed:', rentalErr);
        }
      }

      return dataToUse;
    }

    // RPC returned successfully — normalize the response
    const rpcRow = Array.isArray(rpcData) ? rpcData[0] : rpcData;

    // RPC may not include user_id/total, so fetch full order row to create notification
    try{
      const { data: fullOrder, error: fullErr } = await supabase
        .from('orders')
        .select('*, order_items(quantity, products(id, is_store_rental, details))')
        .eq('id', orderId)
        .maybeSingle();
      if (!fullErr && fullOrder && fullOrder.user_id) {
        const orderUserId = fullOrder.user_id;
        const storeId = fullOrder.store_id;
        let storeName = 'La tienda';
        if (storeId){
          try{ const s = await getStoreById(storeId); if(s && s.name) storeName = s.name; }catch(e){}
        }
        const total = typeof fullOrder.total === 'number' ? fullOrder.total : (fullOrder.total ? Number(fullOrder.total) : null);
        const totalText = total != null ? formatCurrency(total) : 'un monto';
        const templates: Record<string,string> = {
          pending: `${actorName} marcó tu pedido como pendiente (${storeName}).`,
          assigned: `${actorName} asignó tu pedido (${storeName}).`,
          paid: `${actorName} confirmó que recibió tu pago de ${totalText} (${storeName}).`,
          fulfilled: `${actorName} completó tu pedido (${storeName}).`,
          cancelled: `${actorName} canceló tu pedido (${storeName}).`,
          ready_for_pickup: `${actorName} indica que tu pedido está listo para ser retirado (${storeName}).`,
          shipping_in_progress: `${actorName} indica que tu pedido está en proceso de envío (${storeName}).`,
        };
        const message = templates[status] || `${actorName} actualizó el estado de tu pedido a: ${status} (${storeName}).`;
        await createNotification(orderUserId, 'order_update', message, `/orden/${orderId}`);

        if (status === 'paid') {
          if (fullOrder.rental_metadata) {
            // rental_metadata está presente → el trigger DB on_store_rental_order_paid
            // (SECURITY DEFINER, AFTER UPDATE on orders) lo maneja automáticamente.
            // NO llamar al RPC desde el frontend para evitar crear tienda duplicada.
            console.log('[StoreRental] rental_metadata presente, trigger DB maneja la creación de tienda.');
          } else {
            // Fallback: rental_metadata ausente (columna no existe o save falló) —
            // detectar alquiler desde order_items para que la creación de tienda funcione igual
            const orderItems = (fullOrder.order_items as any[] | null) ?? [];
            const rentalItems = orderItems.filter((oi: any) => {
              if (oi?.products?.is_store_rental === true) return true;
              try {
                const d = typeof oi?.products?.details === 'string'
                  ? JSON.parse(oi.products.details)
                  : oi?.products?.details;
                return d?.is_store_rental === true;
              } catch { return false; }
            });
            if (rentalItems.length > 0) {
              const paidDays = Math.max(
                rentalItems.reduce((s: number, oi: any) => s + (Number(oi.quantity) || 1), 0),
                1
              );
              console.log('[StoreRental] order_items fallback: paidDays=', paidDays);
              const { error: rentalRpcErr } = await supabase.rpc('register_or_renew_store_rental', {
                p_user_id: orderUserId,
                p_order_id: orderId,
                p_paid_days: paidDays,
                p_store_id: null,
              });
              if (rentalRpcErr) {
                console.warn('[StoreRental] register_or_renew_store_rental (order_items fallback) error:', rentalRpcErr);
              }
            }
          }
        }
      }
    }catch(e){ console.warn('Failed to create order notification', e); }

    return rpcRow;
  } catch (e) {
    console.error('updateOrderStatus error', e);
    throw e;
  }
};
